import React, { useState } from 'react';
import { Box, Button, Popover } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { startOfDay } from 'date-fns';
import DateRangePicker, { getPresetRange } from 'src/components/UI/DateRangePicker';
import type { DateRangePickerProps } from 'src/components/UI/DateRangePicker';
import { useUserDateFormat } from 'src/hooks/useUserDateFormat';


export type DateRangeButtonProps = {
  /** Currently selected option object with { value, label } */
  selectedOption: { value: string; label: string } | null;
  /** The list of preset options to show inside the picker sidebar */
  presets: Array<{ value: string | number; label: string }>;
  /** Called when the user applies a date range from the picker */
  onApply: (startDate: Date, endDate: Date, preset: string) => void;
  /** Optional sx overrides for the trigger button */
  buttonSx?: Record<string, any>;
  /** Custom Zod schema for date range validation. Forwarded to DateRangePicker. */
  schema?: DateRangePickerProps['schema'];
  /** Custom date disable logic. Forwarded to DateRangePicker. */
  isDateDisabled?: DateRangePickerProps['isDateDisabled'];
  /** Custom preset range resolver. Forwarded to DateRangePicker. */
  resolvePresetRange?: DateRangePickerProps['resolvePresetRange'];
};

const DateRangeButton = ({
  selectedOption,
  presets,
  onApply,
  buttonSx = {},
  schema,
  isDateDisabled,
  resolvePresetRange,
}: DateRangeButtonProps) => {
  const { userDateFormat } = useUserDateFormat();
  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  const dashboardDataFilter = useSelector(
    (state: RootState) => state.dashboard.dashboardDataFilter
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [initialDates, setInitialDates] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });

  const isOpen = Boolean(anchorEl);
  const popoverId = isOpen ? 'date-range-popover' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);

    // Compute initial dates from the current filter state
    if (dashboardDataFilter?.startDate && dashboardDataFilter?.endDate) {
      setInitialDates({
        start: startOfDay(new Date(dashboardDataFilter.startDate)),
        end: startOfDay(new Date(dashboardDataFilter.endDate)),
      });
    } else {
      const range = resolvePresetRange
        ? resolvePresetRange(selectedOption.value)
        : getPresetRange(selectedOption.value, userDetails);
      setInitialDates({ start: range.start, end: range.end });
    }

    setPickerKey((k) => k + 1);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = (startDate: Date, endDate: Date, preset: string) => {
    const startDateString = dayjs(startDate).format('YYYY-MM-DD');
    const endDateString = dayjs(endDate).format('YYYY-MM-DD');
    setInitialDates({
      start: new Date(startDateString),
      end: new Date(endDateString),
    });
    onApply(startDate, endDate, preset);
    setAnchorEl(null);
  };

  // Determine button label
  const getButtonLabel = () => {
    if (selectedOption?.value && selectedOption.value !== 'custom_date') {
      return selectedOption.label;
    }
    if (dashboardDataFilter?.startDate && dashboardDataFilter?.endDate) {
      return `${dayjs(dashboardDataFilter.startDate).format(userDateFormat)} - ${dayjs(dashboardDataFilter.endDate).format(userDateFormat)}`;
    }
    return selectedOption?.label ?? '';
  };

  return (
    <>
      <Button
        aria-describedby={popoverId}
        variant="outlined"
        onClick={handleClick}
        sx={{
          height: '40px',
          fontFamily: 'Inter',
          fontSize: 'var(--font-size-14)',
          fontWeight: 400,
          textTransform: 'none',
          letterSpacing: 0,
          backgroundColor: 'var(--color-white)',
          ...buttonSx,
          border: '1px solid var(--color-border-muted)',
          "&:hover": {
            border: '1px solid var(--color-border-muted)',
            backgroundColor: 'var(--color-white)',
          }
        }}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 'var(--font-size-20)', transition: 'transform 0.2s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
      >
        {getButtonLabel()}
      </Button>
      <Popover
        id={popoverId}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { mt: 0.5 },
        }}
      >
        <Box
          sx={{
            maxWidth: '760px',
            width: { xs: 'calc(100vw - 32px)', sm: '100%' },
            margin: '0 auto',
          }}
        >
          <DateRangePicker
            key={pickerKey}
            initialStartDate={initialDates.start}
            initialEndDate={initialDates.end}
            onApply={handleApply}
            onCancel={handleClose}
            presets={presets}
            schema={schema}
            isDateDisabled={isDateDisabled}
            resolvePresetRange={resolvePresetRange}
          />
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeButton;
